import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Zap, Volume2, MessageSquare, Activity, TrendingUp, Clock, DollarSign } from '../icons';
import { fetchNlpPracticeUsage, type NlpPracticeUsageSummary, VOICE_COST_PER_MINUTE, CHAT_COST_PER_1K_TOKENS } from '../../lib/partner-dashboard';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';

export function NlpPracticeUsagePanel() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [usage, setUsage] = useState<NlpPracticeUsageSummary | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  const load = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    const { data: partner } = await supabase
      .from('partners')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle();
    if (!partner) {
      setIsLoading(false);
      return;
    }
    const data = await fetchNlpPracticeUsage(partner.id, selectedMonth);
    setUsage(data);
    setIsLoading(false);
  }, [user, selectedMonth]);

  useEffect(() => { load(); }, [load]);

  const monthLabel = selectedMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  const monthOptions: Date[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    monthOptions.push(d);
  }

  if (isLoading) return <LoadingSpinner message="Loading NLP Practice usage..." />;

  if (!usage || usage.coaches.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
      >
        <div className="flex items-center gap-2 mb-2">
          <Activity className="w-5 h-5 text-teal-600" />
          <h2 className="text-lg font-semibold text-slate-900">NLP Practice AI Usage</h2>
        </div>
        <p className="text-sm text-slate-500">No coaches found.</p>
      </motion.div>
    );
  }

  const activeCoaches = usage.coaches.filter(c => c.totalSessions > 0);
  const inactiveCoaches = usage.coaches.filter(c => c.totalSessions === 0);
  const maxTokens = Math.max(...usage.coaches.map(c => c.chatTokens), 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-teal-600" />
          <h2 className="text-lg font-semibold text-slate-900">NLP Practice AI Usage</h2>
          <span className="text-xs text-slate-400">— {monthLabel}</span>
        </div>
        <select
          value={selectedMonth.toISOString()}
          onChange={(e) => setSelectedMonth(new Date(e.target.value))}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700"
        >
          {monthOptions.map(d => (
            <option key={d.toISOString()} value={d.toISOString()}>
              {d.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </option>
          ))}
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-teal-50 rounded-xl">
          <div className="flex items-center gap-1.5 mb-1">
            <Zap className="w-4 h-4 text-teal-600" />
            <span className="text-xs text-teal-600 font-medium">Chat Tokens</span>
          </div>
          <div className="text-2xl font-bold text-teal-700">{usage.totalChatTokens.toLocaleString()}</div>
        </div>
        <div className="p-4 bg-amber-50 rounded-xl">
          <div className="flex items-center gap-1.5 mb-1">
            <Volume2 className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-amber-600 font-medium">Voice Sessions</span>
          </div>
          <div className="text-2xl font-bold text-amber-700">{usage.totalVoiceSessions}</div>
        </div>
        <div className="p-4 bg-orange-50 rounded-xl">
          <div className="flex items-center gap-1.5 mb-1">
            <Clock className="w-4 h-4 text-orange-600" />
            <span className="text-xs text-orange-600 font-medium">Voice Minutes</span>
          </div>
          <div className="text-2xl font-bold text-orange-700">{usage.totalVoiceMinutes.toFixed(1)}</div>
        </div>
        <div className="p-4 bg-blue-50 rounded-xl">
          <div className="flex items-center gap-1.5 mb-1">
            <MessageSquare className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-600 font-medium">Chat Sessions</span>
          </div>
          <div className="text-2xl font-bold text-blue-700">{usage.totalChatSessions}</div>
        </div>
        <div className="p-4 bg-emerald-50 rounded-xl">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-emerald-600 font-medium">Active Coaches</span>
          </div>
          <div className="text-2xl font-bold text-emerald-700">{usage.activeCoaches}</div>
        </div>
        <div className="p-4 bg-rose-50 rounded-xl">
          <div className="flex items-center gap-1.5 mb-1">
            <DollarSign className="w-4 h-4 text-rose-600" />
            <span className="text-xs text-rose-600 font-medium">Est. AI Cost</span>
          </div>
          <div className="text-2xl font-bold text-rose-700">${usage.totalCost.toFixed(2)}</div>
          <div className="text-xs text-rose-400 mt-0.5">
            ${usage.totalVoiceCost.toFixed(2)} voice + ${usage.totalChatCost.toFixed(2)} chat
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-400 mb-4">
        Estimated costs based on ${VOICE_COST_PER_MINUTE.toFixed(2)}/min for voice and ${CHAT_COST_PER_1K_TOKENS.toFixed(2)}/1K tokens for chat.
      </p>

      {/* Per-coach table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-3 px-4 font-semibold text-slate-700">Coach</th>
              <th className="text-right py-3 px-4 font-semibold text-slate-700">Chat Tokens</th>
              <th className="text-right py-3 px-4 font-semibold text-slate-700">Chat Sessions</th>
              <th className="text-right py-3 px-4 font-semibold text-slate-700">Voice Sessions</th>
              <th className="text-right py-3 px-4 font-semibold text-slate-700">Voice Min</th>
              <th className="text-right py-3 px-4 font-semibold text-slate-700">Total</th>
              <th className="text-right py-3 px-4 font-semibold text-slate-700">Est. Cost</th>
              <th className="text-left py-3 px-4 font-semibold text-slate-700">Usage</th>
            </tr>
          </thead>
          <tbody>
            {activeCoaches.map(row => (
              <tr key={row.coachId} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4 font-medium text-slate-800">{row.coachName}</td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-teal-500" />
                    <span className="font-medium text-slate-800">{row.chatTokens.toLocaleString()}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-right text-slate-700">{row.chatSessions}</td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Volume2 className="w-3.5 h-3.5 text-amber-500" />
                    <span className="font-medium text-slate-800">{row.voiceSessions}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-orange-500" />
                    <span className="font-medium text-slate-800">{row.voiceMinutes > 0 ? row.voiceMinutes.toFixed(1) : '—'}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-right font-semibold text-slate-900">{row.totalSessions}</td>
                <td className="py-3 px-4 text-right font-semibold text-rose-700">${row.totalCost.toFixed(2)}</td>
                <td className="py-3 px-4 w-32">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-500 rounded-full transition-all"
                      style={{ width: `${Math.max((row.chatTokens / maxTokens) * 100, 2)}%` }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {inactiveCoaches.length > 0 && (
              <>
                <tr>
                  <td colSpan={8} className="py-2 px-4 text-xs font-medium text-slate-400 uppercase tracking-wide">
                    No usage this month ({inactiveCoaches.length})
                  </td>
                </tr>
                {inactiveCoaches.map(row => (
                  <tr key={row.coachId} className="border-b border-slate-50 hover:bg-slate-50 opacity-50">
                    <td className="py-2.5 px-4 text-slate-500">{row.coachName}</td>
                    <td className="py-2.5 px-4 text-right text-slate-300">—</td>
                    <td className="py-2.5 px-4 text-right text-slate-300">—</td>
                    <td className="py-2.5 px-4 text-right text-slate-300">—</td>
                    <td className="py-2.5 px-4 text-right text-slate-300">—</td>
                    <td className="py-2.5 px-4 text-right text-slate-300">0</td>
                    <td className="py-2.5 px-4 text-right text-slate-300">$0.00</td>
                    <td className="py-2.5 px-4 w-32">
                      <div className="h-2 bg-slate-100 rounded-full" />
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}
